// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Search } from '@mui/icons-material';

import { PRIVATE } from 'router/routes';
import { openDrawer, closeDrawer } from 'state/ducks/drawer';
import {
  setSearchSameCompany,
  setSearchOtherCompanies,
} from 'state/ducks/talents';

import useAPI from 'hooks/useAPI';
import useOutsideClick from 'hooks/useOutsideClick';
import { useNavigate } from 'react-router-dom';
import { useCompany } from 'utils/contexts/app-context';
import { useFlags } from 'providers/FlagsProvider';
import FeatureFlags from 'utils/feature-flags';
import styles from './styles.module.scss';
import Suggestions from './suggestions';

const MIN_SEARCH_QUERY_LENGTH = 3;
const SEARCH_PAGE_SIZE = 20;

type Props = {
  count: number,
  results: Talent[],
  setSameCompanyResults: (talents: Talent[]) => void,
  setOtherCompaniesResults: (talents: Talent[]) => void,
  openDrawer: (talent: Talent, sameCompany: boolean) => void,
  closeDrawer: () => void,
  onFocus: () => void,
  onBlur: () => void,
  isMobileBarOpen?: boolean,
  isDrawerOpen: boolean,
};

const getSearchResults = async (API, options) => {
  const response = await API.get('validations/search', {
    params: {
      query: options.value,
      page: options.page,
      pageSize: SEARCH_PAGE_SIZE,
    },
  });
  return response.data;
};

/** ************************************************
 * SearchBar component
 ************************************************* */

const SearchBar = (props: Props) => {
  const navigate = useNavigate();
  /** Network helper */
  const API = useAPI();
  const [ref, didClickOutside] = useOutsideClick();
  const inputRef = React.useRef();
  const { t } = useTranslation();

  const company = useCompany();

  /** State */
  const [loading, setLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [searchError, setSearchError] = React.useState(false);
  const requestId = React.useRef(0);
  const inFlight = React.useRef(false);
  const nextPage = React.useRef(1);
  const [value, setValue] = React.useState('');
  const [hasFocus, setFocus] = React.useState<boolean>(false);
  const [index, setIndex] = React.useState<number>(0);
  const [sameCompanyResults, setSameCompanyResults] = React.useState<Talent[]>(
    []
  );
  const [otherCompaniesResults, setOtherCompaniesResults] = React.useState<
    Talent[]
  >([]);
  const [listNotice, setListNotice] = React.useState<
    null | 'MIN_CHARS' | 'NO_RESULTS'
  >(null);
  const results = React.useMemo(
    () => [...sameCompanyResults, ...otherCompaniesResults],
    [sameCompanyResults, otherCompaniesResults]
  );
  const { matchedFlags } = useFlags({
    authorizedFlags: [
      FeatureFlags.AllowSearchAll,
      FeatureFlags.AllowTalents,
      FeatureFlags.AllowValidations,
    ],
  });

  const fetchData = React.useCallback(
    async (query, page, generation) => {
      if (!company || inFlight.current) return;
      inFlight.current = true;
      setLoading(true);
      setSearchError(false);
      try {
        const data = await getSearchResults(API, { value: query, page });
        if (generation !== requestId.current) return;
        setSameCompanyResults(previous => {
          const combined =
            page === 1 ? data.validations : [...previous, ...data.validations];
          const seen = new Set();
          return combined.filter(result => {
            const id = result.validation.uuid;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
        });
        nextPage.current = page + 1;
        setHasMore(data.hasMore === true);
        setListNotice(
          data.validations.length === 0 && page === 1 ? 'NO_RESULTS' : null
        );
      } catch (error) {
        if (generation === requestId.current) setSearchError(true);
      } finally {
        if (generation === requestId.current) {
          inFlight.current = false;
          setLoading(false);
        }
      }
    },
    [API, company]
  );

  React.useEffect(() => {
    const generation = ++requestId.current;
    inFlight.current = false;
    nextPage.current = 1;
    setLoading(false);
    setHasMore(false);
    setSearchError(false);
    setSameCompanyResults([]);
    setOtherCompaniesResults([]);
    setListNotice(null);
    const query = value.trim();
    if (!hasFocus || query.length < MIN_SEARCH_QUERY_LENGTH) return;
    const timer = setTimeout(() => fetchData(query, 1, generation), 200);
    return () => {
      clearTimeout(timer);
      requestId.current = generation + 1;
    };
  }, [value, hasFocus, fetchData]);

  const loadMore = () => {
    if (hasMore && !searchError) {
      fetchData(value.trim(), nextPage.current, requestId.current);
    }
  };

  React.useEffect(() => {
    if (didClickOutside) setFocus(false);
  }, [didClickOutside]);

  const handleChange = e => setValue(e.target.value);

  /** Reset index every time something happens with value or focus */
  React.useEffect(() => {
    setIndex(0);
  }, [value, hasFocus]);

  React.useEffect(() => {
    const showAllVisible =
      Boolean(value) && matchedFlags.includes(FeatureFlags.AllowTalents);
    let maxIndex = -1;
    if (results.length > 0) {
      maxIndex = showAllVisible ? results.length : results.length - 1;
    } else if (showAllVisible) {
      maxIndex = 0;
    }
    setIndex(i => {
      if (maxIndex < 0) return i;
      return i > maxIndex ? maxIndex : i;
    });
  }, [results, value, matchedFlags]);

  /** Reset value every time something happens with focus */
  React.useEffect(() => {
    setValue('');
    setSameCompanyResults([]);
    setOtherCompaniesResults([]);
    setListNotice(null);
  }, [hasFocus, setValue, setSameCompanyResults, setOtherCompaniesResults]);

  /** Blur input when losing focus */
  React.useEffect(() => {
    if (!hasFocus && inputRef.current != null) {
      inputRef.current.blur();
    }
  }, [hasFocus, inputRef]);

  /** Actions */

  const onKeyDown = e => {
    const showAllVisible =
      Boolean(value) && matchedFlags.includes(FeatureFlags.AllowTalents);
    let maxIndex = -1;
    if (results.length > 0) {
      maxIndex = showAllVisible ? results.length : results.length - 1;
    } else if (showAllVisible) {
      maxIndex = 0;
    }
    if (maxIndex < 0) return;

    if (e.keyCode === 38) {
      e.preventDefault();
      setIndex(index <= 0 ? maxIndex : index - 1);
    } else if (e.keyCode === 40) {
      e.preventDefault();
      setIndex(index >= maxIndex ? 0 : index + 1);
    }
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    const trim = (value || '').trim();

    if (trim.length < MIN_SEARCH_QUERY_LENGTH) {
      setListNotice('MIN_CHARS');
      return;
    }

    if (loading && results.length === 0) {
      return;
    }

    if (results.length === 0) {
      setListNotice('NO_RESULTS');
      return;
    }

    if (
      matchedFlags.includes(FeatureFlags.AllowTalents) &&
      index === results.length
    ) {
      searchAll();
    } else if (results[index]) {
      handleSuggestionClick(results[index]);
    }
  };

  const searchAll = () => {
    props.setSameCompanyResults(sameCompanyResults);
    props.setOtherCompaniesResults(otherCompaniesResults);
    const encodedValue = value.replace(/ /g, '+');
    navigate({
      pathname: PRIVATE.search_results,
      search: `search=${encodedValue}`,
    });
    if (props.isDrawerOpen) {
      props.closeDrawer();
    }
    setFocus(false);
  };

  const handleSuggestionClick = (talent: Talent) => {
    navigate(
      `${
        talent.validation.type === 'LEGAL_ENTITY'
          ? PRIVATE.lev_report
          : PRIVATE.report
      }/${talent.validation.uuid}`
    );
    setFocus(false);
  };

  /** Component */
  const showSuggestions =
    hasFocus &&
    value.trim().length > 0 &&
    (results.length > 0 ||
      loading ||
      searchError ||
      listNotice !== null ||
      matchedFlags.includes(FeatureFlags.AllowTalents));

  return (
    <form
      ref={ref}
      className={`${styles.search_bar} ${
        props.isMobileBarOpen ? styles.clicked : ''
      }`}
      onSubmit={handleFormSubmit}
    >
      <div className={styles.search_bar_contents}>
        <button
          type="button"
          className={styles.search_icon}
          aria-label={t('sentences:search_placeholder')}
          onClick={() =>
            inputRef.current != null ? inputRef.current.focus() : {}
          }
        >
          <Search />
        </button>
        <input
          ref={inputRef}
          className={`${styles.search_input} ${
            props.isMobileBarOpen ? styles.clicked : ''
          }`}
          placeholder={t('sentences:search_placeholder')}
          aria-label={t('sentences:search_placeholder')}
          value={value}
          onChange={handleChange}
          onFocus={() => {
            setFocus(true);
            if (props.onFocus) props.onFocus();
          }}
          onBlur={() => {
            if (props.onBlur) {
              setFocus(false);
              props.onBlur();
            }
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {showSuggestions && (
        <Suggestions
          results={results}
          index={index}
          onSubmit={handleSuggestionClick}
          value={value}
          onShowAll={searchAll}
          showAll={matchedFlags.includes(FeatureFlags.AllowTalents)}
          listNotice={listNotice}
          loading={loading}
          hasMore={hasMore}
          searchError={searchError}
          onLoadMore={loadMore}
          onRetry={() =>
            fetchData(value.trim(), nextPage.current, requestId.current)
          }
        />
      )}
    </form>
  );
};

const mapStateToProps = state => ({
  isDrawerOpen: state.drawer.isOpen,
});

const mapDispatchToProps = dispatch => ({
  openDrawer(talent: Talent, sameCompany: boolean = false) {
    dispatch(openDrawer({ talent, sameCompany }));
  },
  closeDrawer() {
    dispatch(closeDrawer());
  },
  setSameCompanyResults(list: Talent[]) {
    dispatch(setSearchSameCompany({ list }));
  },
  setOtherCompaniesResults(list: Talent[]) {
    dispatch(setSearchOtherCompanies({ list }));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchBar);
